import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { CategorieNdf, CompteAnalytique, LigneBareme, ParametreNdf, RefValeur } from '../../types/ndf';
import {
  Bascule,
  BoutonPrincipal,
  BoutonSecondaire,
  Champ,
  Bloc,
  Etiquette,
  Saisie,
  Section,
  ValeurCalculee,
  Selection,
} from '../../components/ndf/ui/Champ';
import { useZohoItems } from '../../hooks/useZohoItems';
import { useZohoAccounts } from '../../hooks/useZohoAccounts';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { MessageErreur, MessageVide } from '../../components/ndf/ui/MessageEtat';
import { RegularisationIk } from '../../components/ndf/RegularisationIk';
import { ExportComptable } from '../../components/ndf/ExportComptable';
import type { TeinteSection } from '../../components/ndf/ui/Champ';
import { peutNdf } from '../../types/ndf';
import { useToast } from '../../context/ToastContext';
import { apercuActif } from '../../services/apercu';

/**
 * Paramétrage du module Notes de frais — « le back ».
 *
 * Tout ce qui est administrable vit en base : barème, catégories, comptes,
 * listes, seuils. Corriger un taux ou un libellé Zoho doit être une saisie,
 * pas un déploiement.
 */

type Onglet =
  | 'bareme'
  | 'categories'
  | 'comptes'
  | 'listes'
  | 'parametres'
  | 'regularisation'
  | 'export';

// Une teinte par domaine : on se repère à la couleur avant de lire le titre.
const ONGLETS: Array<{ cle: Onglet; libelle: string; teinte: TeinteSection }> = [
  { cle: 'bareme', libelle: 'Barème kilométrique', teinte: 'violet' },
  { cle: 'categories', libelle: 'Catégories de dépenses', teinte: 'vert' },
  { cle: 'comptes', libelle: 'Comptes analytiques', teinte: 'jaune' },
  { cle: 'listes', libelle: 'Listes de valeurs', teinte: 'ciel' },
  { cle: 'parametres', libelle: 'Seuils et délais', teinte: 'corail' },
  { cle: 'regularisation', libelle: 'Régularisation IK', teinte: 'violet' },
  { cle: 'export', libelle: 'Export comptable', teinte: 'jaune' },
];

export function NdfParametragePage() {
  const [onglet, setOnglet] = useState<Onglet>('bareme');
  const { data: moi, error: erreurMoi } = useQuery({
    queryKey: ['ndf', 'moi', apercuActif()],
    queryFn: () => ndfApi.moi(),
    retry: false,
  });

  if (erreurMoi) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <MessageErreur erreur={erreurMoi} />
      </div>
    );
  }

  if (moi && !peutNdf(moi, 'administrer')) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p className="rounded-lg border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-4 text-sm text-jaune">
          Le paramétrage du module est réservé aux administrateurs. Vous pouvez consulter les
          fiches salariés depuis l’onglet « Salariés ».
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <header className="mb-4">
        <h1 className="font-titre text-xl font-bold text-nuit">Paramétrage</h1>
        <p className="text-sm text-nuit/70">
          Barème, catégories et seuils du module Notes de frais.
        </p>
      </header>

      <nav className="mb-4 flex flex-wrap gap-2">
        {ONGLETS.map((o) => (
          <button
            key={o.cle}
            type="button"
            onClick={() => setOnglet(o.cle)}
            className={`min-h-11 rounded-lg px-3 text-sm font-medium transition-all ${
              onglet === o.cle
                ? 'bg-nuit text-white shadow-sm'
                : 'bg-white text-nuit ring-1 ring-nuit/15 hover:ring-nuit/30 active:translate-y-px'
            }`}
          >
            {o.libelle}
          </button>
        ))}
      </nav>

      {onglet === 'bareme' && <OngletBareme />}
      {onglet === 'categories' && <OngletCategories />}
      {onglet === 'comptes' && <OngletComptes />}
      {onglet === 'listes' && <OngletListes />}
      {onglet === 'parametres' && <OngletParametres />}
      {onglet === 'regularisation' && <RegularisationIk />}
      {onglet === 'export' && <ExportComptable />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Barème kilométrique
// ════════════════════════════════════════════════════════════════════════════

const LIBELLE_TYPE: Record<string, string> = {
  voiture: 'Voiture',
  deux_roues_50_plus: 'Moto ou scooter de plus de 50 cm³',
  cyclomoteur: 'Cyclomoteur de moins de 50 cm³',
};

const LIBELLE_CV: Record<string, string> = {
  cv_3_moins: '3 CV et moins',
  cv_4: '4 CV',
  cv_5: '5 CV',
  cv_6: '6 CV',
  cv_7_plus: '7 CV et plus',
  cv_1_2: '1 à 2 CV',
  cv_3_5: '3 à 5 CV',
  cv_5_plus: 'Plus de 5 CV',
};

function libelleTranche(l: LigneBareme) {
  const min = l.distance_min.toLocaleString('fr-FR');
  if (l.distance_max === null) return `Au-delà de ${min} km`;
  return `De ${min} à ${l.distance_max.toLocaleString('fr-FR')} km`;
}

function OngletBareme() {
  const toast = useToast();
  const qc = useQueryClient();

  // On lit TOUT le barème d'un coup : cela donne la liste réelle des années
  // saisies, plutôt qu'une liste devinée autour de l'année en cours.
  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'bareme', 'tout'],
    queryFn: () => ndfApi.bareme.lireTout(),
    retry: false,
  });

  const toutes = data?.bareme ?? [];
  const anneesSaisies = [...new Set(toutes.map((l) => l.annee))].sort((a, b) => b - a);
  const [annee, setAnnee] = useState<number | null>(null);
  const anneeCourante = annee ?? anneesSaisies[0] ?? new Date().getFullYear();
  const lignes = toutes.filter((l) => l.annee === anneeCourante);

  const dupliquer = useMutation({
    mutationFn: (cible: number) => ndfApi.bareme.dupliquer(anneeCourante, cible),
    onSuccess: (r, cible) => {
      toast.success(
        `${r.lignes_creees} lignes recopiées sur ${cible}. Vérifiez les taux publiés avant de vous en servir.`,
      );
      setAnnee(cible);
      qc.invalidateQueries({ queryKey: ['ndf', 'bareme'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La recopie a échoué.'),
  });

  const modifier = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<LigneBareme> }) =>
      ndfApi.bareme.modifier(id, body),
    onSuccess: () => {
      toast.success('Taux enregistré.');
      qc.invalidateQueries({ queryKey: ['ndf', 'bareme'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  const [edition, setEdition] = useState(false);

  const groupes = new Map<string, LigneBareme[]>();
  for (const l of lignes) {
    const cle = `${l.type_vehicule}|${l.tranche_cv ?? ''}|${l.electrique}`;
    if (!groupes.has(cle)) groupes.set(cle, []);
    groupes.get(cle)!.push(l);
  }

  // La prochaine année à créer : celle qui suit la plus récente déjà saisie.
  const prochaineAnnee = (anneesSaisies[0] ?? new Date().getFullYear()) + 1;
  const prochaineExiste = anneesSaisies.includes(prochaineAnnee);

  return (
    <Section
      titre={`Barème ${anneeCourante}`}
      teinte="violet"
      description="Ces taux fondent le calcul des indemnités kilométriques. Ils ne sont écrits nulle part dans le code : ce tableau fait foi."
    >
      <div className="flex flex-wrap items-end gap-3">
        <Champ label="Année">
          <Selection
            value={anneeCourante}
            onChange={(e) => {
              setAnnee(Number(e.target.value));
              setEdition(false);
            }}
          >
            {(anneesSaisies.length ? anneesSaisies : [anneeCourante]).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Selection>
        </Champ>

        {/* Le bouton suit toujours la dernière année saisie : en 2027 il
            proposera 2028, sans rien à modifier dans le code. */}
        {!prochaineExiste && lignes.length > 0 && (
          <button
            type="button"
            onClick={() => dupliquer.mutate(prochaineAnnee)}
            disabled={dupliquer.isPending}
            className="min-h-11 rounded-md px-3 text-sm font-medium text-blue-700 ring-1 ring-blue-200 hover:bg-blue-50 disabled:opacity-50"
          >
            {dupliquer.isPending ? 'Copie en cours…' : `Créer le barème ${prochaineAnnee}`}
          </button>
        )}

        {lignes.length > 0 && (
          <button
            type="button"
            onClick={() => setEdition((v) => !v)}
            className="min-h-11 rounded-md px-3 text-sm font-medium text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50"
          >
            {edition ? 'Terminer la modification' : 'Modifier les taux'}
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-slate-500">Chargement du barème…</p>}
      {error && <MessageErreur erreur={error} />}

      {!isLoading && !error && lignes.length === 0 && (
        <MessageVide>
          Aucun barème saisi pour {anneeCourante}. Sans lui, aucune indemnité kilométrique ne peut
          être calculée.
        </MessageVide>
      )}

      {edition && (
        <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-3 text-sm text-jaune">
          Chaque taux est enregistré dès que vous quittez le champ. Reportez les valeurs publiées
          par l’administration : elles servent de justificatif en cas de contrôle.
        </p>
      )}

      <div className="space-y-3">
        {[...groupes.entries()].map(([cle, groupe]) => {
          const premiere = groupe[0]!;
          return (
            <div key={cle} className="rounded-md border border-slate-200">
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
                <span className="text-sm font-medium text-slate-800">
                  {LIBELLE_TYPE[premiere.type_vehicule] ?? premiere.type_vehicule}
                </span>
                {premiere.tranche_cv && (
                  <Etiquette>{LIBELLE_CV[premiere.tranche_cv] ?? premiere.tranche_cv}</Etiquette>
                )}
                {premiere.electrique ? (
                  <Etiquette ton="positif">100 % électrique</Etiquette>
                ) : (
                  <Etiquette>Thermique et hybride</Etiquette>
                )}
              </div>
              <ul className="divide-y divide-slate-100">
                {groupe
                  .slice()
                  .sort((a, b) => a.distance_min - b.distance_min)
                  .map((l) => (
                    <li
                      key={l.id}
                      className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                    >
                      <span className="text-sm text-slate-600">{libelleTranche(l)}</span>
                      {edition ? (
                        <span className="flex items-center gap-2 text-sm text-slate-600">
                          distance ×
                          <Saisie
                            className="w-24"
                            inputMode="decimal"
                            defaultValue={l.coefficient}
                            onBlur={(e) => {
                              const v = Number(e.target.value.replace(',', '.'));
                              if (Number.isFinite(v) && v > 0 && v !== l.coefficient) {
                                modifier.mutate({ id: l.id, body: { coefficient: v } });
                              }
                            }}
                          />
                          +
                          <Saisie
                            className="w-24"
                            inputMode="decimal"
                            defaultValue={l.part_fixe}
                            onBlur={(e) => {
                              const v = Number(e.target.value.replace(',', '.'));
                              if (Number.isFinite(v) && v >= 0 && v !== l.part_fixe) {
                                modifier.mutate({ id: l.id, body: { part_fixe: v } });
                              }
                            }}
                          />
                          €
                        </span>
                      ) : (
                        <span className="font-mono text-sm text-slate-900">
                          {l.part_fixe > 0
                            ? `(distance × ${l.coefficient.toLocaleString('fr-FR', { minimumFractionDigits: 3 })}) + ${l.part_fixe.toLocaleString('fr-FR')} €`
                            : `distance × ${l.coefficient.toLocaleString('fr-FR', { minimumFractionDigits: 3 })}`}
                        </span>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          );
        })}
      </div>

    </Section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Catégories de dépenses et sous-catégories
// ════════════════════════════════════════════════════════════════════════════

/** Un code technique stable, déduit du libellé — l'utilisateur ne le saisit pas. */
function codeDepuisLibelle(libelle: string, prefixe = '') {
  const base = libelle
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return prefixe ? `${prefixe}_${base}` : base;
}

/**
 * Une teinte par catégorie, attribuée par position.
 *
 * Le but est de DISTINGUER les blocs, pas de coder une information : rien ne
 * dépend de la couleur, elle sert uniquement de repère visuel dans une liste
 * longue.
 */
const TEINTES_CATEGORIES: TeinteSection[] = ['ciel', 'vert', 'violet', 'jaune', 'corail'];

function OngletCategories() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'categories'],
    queryFn: () => ndfApi.categories.lire(),
    retry: false,
  });
  const categories: CategorieNdf[] = data?.categories ?? [];
  const racines = categories.filter((c) => !c.parent_id);
  const enfantsDe = (id: string) => categories.filter((c) => c.parent_id === id);

  const [ajoutSous, setAjoutSous] = useState<string | null>(null);
  const [nouvelle, setNouvelle] = useState('');

  const invalider = () => qc.invalidateQueries({ queryKey: ['ndf', 'categories'] });

  const creer = useMutation({
    mutationFn: (body: Partial<CategorieNdf>) => ndfApi.categories.creer(body),
    onSuccess: () => {
      toast.success('Catégorie ajoutée.');
      setNouvelle('');
      setAjoutSous(null);
      invalider();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’ajout a échoué.'),
  });

  const modifier = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CategorieNdf> }) =>
      ndfApi.categories.modifier(id, body),
    onSuccess: () => invalider(),
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  return (
    <Section
      titre="Catégories de dépenses"
      teinte="vert"
      description="La règle de TVA d’une catégorie verrouille le champ TVA du formulaire de dépense. Une catégorie désactivée n’est plus proposée aux salariés, mais reste sur les dépenses passées."
    >
      {isLoading && <p className="text-sm text-slate-500">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      <div className="space-y-2">
        {racines.map((c, index) => {
          const enfants = enfantsDe(c.id);
          const teinte = TEINTES_CATEGORIES[index % TEINTES_CATEGORIES.length]!;
          return (
            <Bloc
              key={c.id}
              titre={c.libelle}
              teinte={teinte}
              inactif={!c.actif}
              barre={!c.actif}
              actions={
                <div className="flex items-center gap-2">
                  {/* Le bouton n'apparaît que là où une subdivision a un sens
                      métier : proposer d'ajouter une sous-catégorie à
                      « Hébergement » laisserait croire à une souplesse qui
                      n'existe pas. Le drapeau est en base, pas dans l'écran. */}
                  {c.accepte_sous_categories && (
                    <BoutonSecondaire
                      type="button"
                      onClick={() => {
                        setAjoutSous(ajoutSous === c.id ? null : c.id);
                        setNouvelle('');
                      }}
                    >
                      Ajouter une sous-catégorie
                    </BoutonSecondaire>
                  )}
                  <Bascule
                    actif={c.actif}
                    libelleActif="Active"
                    libelleInactif="Inactive"
                    onChange={(actif) => modifier.mutate({ id: c.id, body: { actif } })}
                  />
                </div>
              }
            >
              <div className="border-t border-nuit/10 px-3 py-2">
                <span className="flex flex-wrap gap-1">
                  {c.regle_tva === 'forcee_zero' ? (
                    <Etiquette ton="alerte">TVA forcée à 0</Etiquette>
                  ) : (
                    <Etiquette>TVA saisie</Etiquette>
                  )}
                  {c.sous_categorie_obligatoire && <Etiquette>Sous-catégorie obligatoire</Etiquette>}
                  {c.quantite_defaut !== 1 && (
                    <Etiquette ton="actif">Quantité par défaut {c.quantite_defaut}</Etiquette>
                  )}
                  {c.champ_invites && <Etiquette>Champ « Invités »</Etiquette>}
                  {c.est_ik && <Etiquette ton="actif">Indemnités kilométriques</Etiquette>}
                </span>
              </div>

              {enfants.length > 0 && (
                <ul className="divide-y divide-nuit/5 border-t border-nuit/10 px-3">
                  {enfants.map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-2">
                      <span className={`text-sm ${s.actif ? 'text-nuit' : 'text-nuit/40 line-through'}`}>
                        {s.libelle}
                      </span>
                      <Bascule
                        actif={s.actif}
                        libelleActif="Active"
                        libelleInactif="Inactive"
                        onChange={(actif) => modifier.mutate({ id: s.id, body: { actif } })}
                      />
                    </li>
                  ))}
                </ul>
              )}

              {ajoutSous === c.id && (
                <div className="flex flex-wrap items-end gap-2 border-t border-nuit/10 p-3">
                  <Champ label={`Nouvelle sous-catégorie de « ${c.libelle} »`}>
                    <Saisie
                      autoFocus
                      value={nouvelle}
                      onChange={(e) => setNouvelle(e.target.value)}
                      placeholder="Par exemple : Covoiturage"
                    />
                  </Champ>
                  <BoutonPrincipal
                    type="button"
                    disabled={!nouvelle.trim() || creer.isPending}
                    onClick={() =>
                      creer.mutate({
                        parent_id: c.id,
                        code: codeDepuisLibelle(nouvelle, c.code),
                        libelle: nouvelle.trim(),
                        // La sous-catégorie hérite de la règle de TVA du parent :
                        // sur « Transport », la TVA reste forcée à zéro.
                        regle_tva: c.regle_tva,
                      })
                    }
                  >
                    Ajouter
                  </BoutonPrincipal>
                  <BoutonSecondaire
                    type="button"
                    onClick={() => {
                      setAjoutSous(null);
                      setNouvelle('');
                    }}
                  >
                    Annuler
                  </BoutonSecondaire>
                </div>
              )}
            </Bloc>
          );
        })}
      </div>

      <NouvelleCategorie
        enCours={creer.isPending}
        onCreer={(libelle, regleTva) =>
          creer.mutate({ code: codeDepuisLibelle(libelle), libelle, regle_tva: regleTva })
        }
      />
    </Section>
  );
}

function NouvelleCategorie({
  enCours,
  onCreer,
}: {
  enCours: boolean;
  onCreer: (libelle: string, regleTva: 'saisie' | 'forcee_zero') => void;
}) {
  const [libelle, setLibelle] = useState('');
  const [regleTva, setRegleTva] = useState<'saisie' | 'forcee_zero'>('saisie');

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-dashed border-slate-300 p-3">
      <Champ label="Nouvelle catégorie">
        <Saisie
          value={libelle}
          onChange={(e) => setLibelle(e.target.value)}
          placeholder="Par exemple : Formation"
        />
      </Champ>
      <Champ label="TVA" aide="« Forcée à 0 » verrouille le champ TVA pour le salarié.">
        <Selection value={regleTva} onChange={(e) => setRegleTva(e.target.value as 'saisie' | 'forcee_zero')}>
          <option value="saisie">Saisie par le salarié</option>
          <option value="forcee_zero">Forcée à 0</option>
        </Selection>
      </Champ>
      <BoutonPrincipal
        type="button"
        disabled={!libelle.trim() || enCours}
        onClick={() => {
          onCreer(libelle.trim(), regleTva);
          setLibelle('');
        }}
      >
        Ajouter
      </BoutonPrincipal>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Comptes analytiques
// ════════════════════════════════════════════════════════════════════════════

function OngletComptes() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'comptes'],
    queryFn: () => ndfApi.comptes.lire(),
    retry: false,
  });
  // Référentiels lus directement dans Zoho Books : choisir dans une liste
  // supprime le risque du libellé recopié « au caractère près », qui ne se
  // verrait qu'au moment de créer une vraie facture.
  const items = useZohoItems();
  const comptesBooks = useZohoAccounts();
  // Les hooks renvoient l'enveloppe complète de l'API ({ items, total }).
  const articles = items.data?.items ?? [];
  const comptesAchat = comptesBooks.data?.accounts ?? [];

  const comptes: CompteAnalytique[] = data?.comptes ?? [];

  const modifier = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CompteAnalytique> }) =>
      ndfApi.comptes.modifier(id, body),
    onSuccess: () => {
      toast.success('Compte enregistré.');
      setEdition(null);
      setBrouillon({});
      qc.invalidateQueries({ queryKey: ['ndf', 'comptes'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  const referentielsIndisponibles = items.isError || comptesBooks.isError;

  // Même règle que partout : on consulte, puis on modifie explicitement.
  // `brouillon` retient les choix jusqu'à l'enregistrement, pour que « Annuler »
  // ait quelque chose à annuler.
  const [edition, setEdition] = useState<string | null>(null);
  const [brouillon, setBrouillon] = useState<Partial<CompteAnalytique>>({});

  return (
    <Section
      titre="Comptes analytiques"
      teinte="jaune"
      description="Chaque compte comptable est rattaché à un article de Zoho Books et à son compte d’achat. C’est ce rattachement qui permettra de créer la facture de remboursement."
    >
      {isLoading && <p className="text-sm text-slate-500">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      {referentielsIndisponibles && (
        <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-3 text-sm text-jaune">
          La liste des articles Zoho Books n’a pas pu être chargée. Vous pouvez revenir plus tard :
          les rattachements déjà enregistrés sont conservés.
        </p>
      )}

      <div className="space-y-4">
        {comptes.map((c) => {
          const enEdition = edition === c.id;
          const article = enEdition ? (brouillon.zoho_item_name ?? c.zoho_item_name) : c.zoho_item_name;
          const compteAchat = enEdition
            ? (brouillon.zoho_account_name ?? c.zoho_account_name)
            : c.zoho_account_name;

          return (
            <Bloc
              key={c.id}
              titre={`${c.code} : ${c.libelle}`}
              teinte="jaune"
              actions={
                !c.article_requis ? (
                  <Etiquette>Sans article : export comptable</Etiquette>
                ) : !c.zoho_item_name ? (
                  <Etiquette ton="alerte">Article non rattaché</Etiquette>
                ) : null
              }
            >
              <div className="space-y-3 border-t border-nuit/10 p-3">
                {!enEdition ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {c.article_requis ? (
                      <Champ label="Article Zoho Books">
                        <ValeurCalculee>{article ?? ''}</ValeurCalculee>
                      </Champ>
                    ) : (
                      <Champ
                        label="Article Zoho Books"
                        aide="Ce compte ne porte pas d’article : la TVA est un taux appliqué sur la ligne de facture. Il ne sert qu’à produire le fichier de l’expert-comptable."
                      >
                        <ValeurCalculee>Sans objet</ValeurCalculee>
                      </Champ>
                    )}
                    <Champ label="Compte d’achat Zoho Books">
                      <ValeurCalculee>{compteAchat ?? ''}</ValeurCalculee>
                    </Champ>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {c.article_requis && (
                    <Champ
                      label="Article Zoho Books"
                      aide={items.isLoading ? 'Chargement des articles…' : undefined}
                    >
                      <Selection
                        value={article ?? ''}
                        disabled={items.isLoading || items.isError}
                        onChange={(e) => {
                          const nom = e.target.value || null;
                          const item = articles.find((i) => i.name === nom);
                          setBrouillon((b) => ({
                            ...b,
                            zoho_item_name: nom,
                            // Le compte d'ACHAT de l'article est proposé
                            // automatiquement : c'est lui qui compte pour une
                            // facture fournisseur, jamais le compte de vente.
                            // Il reste modifiable juste à côté — c'est ce qui
                            // permet au même article de servir plusieurs
                            // comptes analytiques.
                            zoho_account_name: item?.purchase_account_name ?? b.zoho_account_name ?? null,
                          }));
                        }}
                      >
                        <option value="">Aucun article rattaché</option>
                        {articles.map((i) => (
                          <option key={i.id} value={i.name}>
                            {i.name}
                          </option>
                        ))}
                      </Selection>
                    </Champ>
                    )}
                    <Champ
                      label="Compte d’achat Zoho Books"
                      aide="Proposé d’après l’article, modifiable : le même article peut servir plusieurs comptes."
                    >
                      <Selection
                        value={compteAchat ?? ''}
                        disabled={comptesBooks.isLoading || comptesBooks.isError}
                        onChange={(e) =>
                          setBrouillon((b) => ({ ...b, zoho_account_name: e.target.value || null }))
                        }
                      >
                        <option value="">Aucun compte rattaché</option>
                        {comptesAchat.map((a) => (
                          <option key={a.id} value={a.name}>
                            {a.code ? `${a.code} : ${a.name}` : a.name}
                          </option>
                        ))}
                      </Selection>
                    </Champ>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  {!enEdition ? (
                    <BoutonSecondaire
                      type="button"
                      onClick={() => {
                        setEdition(c.id);
                        setBrouillon({});
                      }}
                    >
                      Modifier
                    </BoutonSecondaire>
                  ) : (
                    <>
                      <BoutonSecondaire
                        type="button"
                        onClick={() => {
                          setEdition(null);
                          setBrouillon({});
                        }}
                      >
                        Annuler
                      </BoutonSecondaire>
                      <BoutonPrincipal
                        type="button"
                        disabled={modifier.isPending}
                        onClick={() => modifier.mutate({ id: c.id, body: brouillon })}
                      >
                        {modifier.isPending ? 'Enregistrement…' : 'Enregistrer'}
                      </BoutonPrincipal>
                    </>
                  )}
                </div>
              </div>
            </Bloc>
          );
        })}
      </div>
    </Section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Listes de valeurs
// ════════════════════════════════════════════════════════════════════════════

const LIBELLE_LISTE: Record<string, string> = {
  civilite: 'Civilités',
  type_contrat: 'Types de contrat',
  statut_rh: 'Statuts RH',
  motif_depart: 'Motifs de départ',
  motorisation: 'Motorisations',
  type_vehicule: 'Types de véhicule',
  statut_mission_exclu: 'Statuts de mission exclus de la refacturation',
};

/** Listes dont les valeurs techniques sont lues par le code : à ne pas renommer. */
const LISTES_SENSIBLES: Record<string, string> = {
  statut_rh: 'Ces valeurs pilotent le calcul des périodes d’essai.',
  motorisation: 'Ces valeurs choisissent la ligne de barème appliquée.',
  type_vehicule: 'Ces valeurs choisissent les tranches de distance du barème.',
};

function OngletListes() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'listes'],
    queryFn: () => ndfApi.listes.lire(),
    retry: false,
  });
  const valeurs: RefValeur[] = data?.valeurs ?? [];

  const [ajoutSur, setAjoutSur] = useState<string | null>(null);
  const [nouveau, setNouveau] = useState('');

  const invalider = () => qc.invalidateQueries({ queryKey: ['ndf', 'listes'] });

  const creer = useMutation({
    mutationFn: (body: Partial<RefValeur>) => ndfApi.listes.creer(body),
    onSuccess: () => {
      toast.success('Valeur ajoutée.');
      setNouveau('');
      setAjoutSur(null);
      invalider();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’ajout a échoué.'),
  });

  const modifier = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<RefValeur> }) =>
      ndfApi.listes.modifier(id, body),
    onSuccess: () => invalider(),
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  // Les sous-catégories de transport ont migré vers l'onglet « Catégories de
  // dépenses ». Les montrer ici aussi donnerait deux endroits pour administrer
  // la même chose — donc deux vérités possibles.
  const LISTES_MIGREES = ['sous_categorie_transport'];

  const parListe = new Map<string, RefValeur[]>();
  for (const v of valeurs) {
    if (LISTES_MIGREES.includes(v.liste)) continue;
    if (!parListe.has(v.liste)) parListe.set(v.liste, []);
    parListe.get(v.liste)!.push(v);
  }

  return (
    <Section
      titre="Listes de valeurs"
      teinte="ciel"
      description="Les choix proposés dans les formulaires. Une valeur désactivée n’est plus proposée, mais reste lisible sur les fiches qui la portent déjà."
    >
      {isLoading && <p className="text-sm text-slate-500">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      <div className="space-y-3">
        {[...parListe.entries()].map(([liste, items], indexListe) => (
          <Bloc
            key={liste}
            titre={LIBELLE_LISTE[liste] ?? liste}
            teinte={TEINTES_CATEGORIES[indexListe % TEINTES_CATEGORIES.length]!}
            actions={
              <BoutonPrincipal
                type="button"
                onClick={() => {
                  setAjoutSur(ajoutSur === liste ? null : liste);
                  setNouveau('');
                }}
              >
                Ajouter une valeur
              </BoutonPrincipal>
            }
          >

            {LISTES_SENSIBLES[liste] && (
              <p className="border-t border-nuit/10 border-l-2 border-l-jaune-fond px-3 py-2 text-xs text-jaune">
                {LISTES_SENSIBLES[liste]} Vous pouvez en ajouter et en désactiver ; évitez de
                renommer les valeurs existantes.
              </p>
            )}

            <ul className="divide-y divide-nuit/5 px-3">
              {items.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-2">
                  <span className={`text-sm ${v.actif ? 'text-nuit' : 'text-nuit/40 line-through'}`}>
                    {v.libelle}
                  </span>
                  <Bascule
                    actif={v.actif}
                    libelleActif="Active"
                    libelleInactif="Inactive"
                    onChange={(actif) => modifier.mutate({ id: v.id, body: { actif } })}
                  />
                </li>
              ))}
            </ul>

            {ajoutSur === liste && (
              <div className="flex flex-wrap items-end gap-2 border-t border-nuit/10 p-3">
                <Champ label="Libellé de la nouvelle valeur">
                  <Saisie autoFocus value={nouveau} onChange={(e) => setNouveau(e.target.value)} />
                </Champ>
                <BoutonPrincipal
                  type="button"
                  disabled={!nouveau.trim() || creer.isPending}
                  onClick={() =>
                    creer.mutate({
                      liste,
                      valeur: codeDepuisLibelle(nouveau),
                      libelle: nouveau.trim(),
                      ordre: (items.at(-1)?.ordre ?? 0) + 10,
                    })
                  }
                >
                  Ajouter
                </BoutonPrincipal>
                <BoutonSecondaire
                  type="button"
                  onClick={() => {
                    setAjoutSur(null);
                    setNouveau('');
                  }}
                >
                  Annuler
                </BoutonSecondaire>
              </div>
            )}
          </Bloc>
        ))}
      </div>
    </Section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Seuils et délais
// ════════════════════════════════════════════════════════════════════════════

const LIBELLE_PARAM: Record<string, { libelle: string; aide: string; unite?: string }> = {
  ndf_delai_declaration_mois: {
    libelle: 'Délai de déclaration d’une dépense',
    aide: 'Au-delà, une alerte s’affiche sans bloquer la déclaration.',
    unite: 'mois',
  },
  ndf_seuil_ecart_devise_pct: {
    libelle: 'Écart de conversion toléré',
    aide: 'Au-delà, l’écart entre le taux du jour et le montant déclaré est signalé.',
    unite: '%',
  },
  ndf_seuil_montant_eleve: {
    libelle: 'Seuil de montant élevé',
    aide: 'Laisser vide pour désactiver l’alerte.',
    unite: '€',
  },
  ndf_delai_relance_valideurs_jours: {
    libelle: 'Relance des valideurs',
    aide: 'Délai avant de rappeler une approbation en attente.',
    unite: 'jours',
  },
  ndf_seuil_alerte_cumul_ik_km: {
    libelle: 'Alerte de cumul kilométrique',
    aide: 'Seuil annuel par salarié au-delà duquel un contrôle est signalé.',
    unite: 'km',
  },
  ndf_jour_export_comptable: {
    libelle: 'Jour de génération de l’export comptable',
    aide: 'Jour du mois.',
  },
  ndf_purge_pdf_jours: {
    libelle: 'Purge des documents générés',
    aide: 'Délai après dépôt Dropbox. Les justificatifs d’origine ne sont jamais supprimés.',
    unite: 'jours',
  },
  ndf_alerte_attente_remboursement_jours: {
    libelle: 'Alerte d’attente de remboursement',
    aide: 'Délai au-delà duquel une note non lettrée dans Books est signalée.',
    unite: 'jours',
  },
};

function OngletParametres() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'parametres'],
    queryFn: () => ndfApi.parametres.lire(),
    retry: false,
  });
  const parametres: ParametreNdf[] = data?.parametres ?? [];

  const [edition, setEdition] = useState(false);
  const [brouillon, setBrouillon] = useState<Record<string, string>>({});

  // Un seul enregistrement pour toute la grille : ces seuils se règlent
  // ensemble, et un bouton par champ multiplierait les gestes sans rien
  // apporter.
  const enregistrer = useMutation({
    mutationFn: async (modifs: Record<string, string>) => {
      for (const [cle, valeur] of Object.entries(modifs)) {
        await ndfApi.parametres.enregistrer(cle, valeur);
      }
      return Object.keys(modifs).length;
    },
    onSuccess: (n) => {
      toast.success(n === 0 ? 'Aucune modification.' : `${n} paramètre(s) enregistré(s).`);
      setEdition(false);
      setBrouillon({});
      qc.invalidateQueries({ queryKey: ['ndf', 'parametres'] });
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’enregistrement a échoué.'),
  });

  return (
    <Section
      titre="Seuils et délais"
      teinte="corail"
      description="Modifiables sans redéploiement."
    >
      {isLoading && <p className="text-sm text-slate-500">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}
      <div className="grid gap-4 sm:grid-cols-2">
        {parametres.map((p) => {
          const meta = LIBELLE_PARAM[p.key];
          const valeur = brouillon[p.key] ?? p.value ?? '';
          return (
            <Champ
              key={p.key}
              label={meta?.libelle ?? p.key}
              aide={meta ? `${meta.aide}${meta.unite ? ` (en ${meta.unite})` : ''}` : undefined}
            >
              {edition ? (
                <Saisie
                  inputMode="decimal"
                  value={valeur}
                  onChange={(e) => setBrouillon((b) => ({ ...b, [p.key]: e.target.value }))}
                />
              ) : (
                <ValeurCalculee>
                  {p.value ? `${p.value}${meta?.unite ? ` ${meta.unite}` : ''}` : ''}
                </ValeurCalculee>
              )}
            </Champ>
          );
        })}
      </div>

      <div className="flex justify-end gap-2">
        {!edition ? (
          <BoutonSecondaire type="button" onClick={() => setEdition(true)}>
            Modifier
          </BoutonSecondaire>
        ) : (
          <>
            <BoutonSecondaire
              type="button"
              onClick={() => {
                setEdition(false);
                setBrouillon({});
              }}
            >
              Annuler
            </BoutonSecondaire>
            <BoutonPrincipal
              type="button"
              disabled={enregistrer.isPending}
              onClick={() => {
                // On n'envoie que ce qui a réellement changé.
                const modifs = Object.fromEntries(
                  Object.entries(brouillon).filter(
                    ([cle, v]) => v !== (parametres.find((p) => p.key === cle)?.value ?? ''),
                  ),
                );
                enregistrer.mutate(modifs);
              }}
            >
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </BoutonPrincipal>
          </>
        )}
      </div>
    </Section>
  );
}
